import { useEffect, useRef, useState } from "react";

// Polls /version.json and reports when the deployed version differs from the
// one this tab loaded with. Designed to be very cheap and silent on failure
// (e.g. if /version.json is missing in dev or behind an offline state).

const DEFAULT_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const ADMIN_INTERVAL_MS = 60 * 1000; // 1 minute on /admin

async function fetchCurrentVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data?.version ?? null;
  } catch {
    return null;
  }
}

export function useAppVersion() {
  const initialVersionRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    let cancelled = false;
    const intervalMs = window.location.pathname.startsWith("/admin")
      ? ADMIN_INTERVAL_MS
      : DEFAULT_INTERVAL_MS;

    const tick = async () => {
      const current = await fetchCurrentVersion();
      if (cancelled || !current) return;
      if (initialVersionRef.current === null) {
        initialVersionRef.current = current;
        return;
      }
      if (current !== initialVersionRef.current) {
        setUpdateAvailable(true);
      }
    };

    void tick();
    const interval = window.setInterval(tick, intervalMs);
    const onFocus = () => void tick();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return { updateAvailable };
}
